import React, { useEffect, useRef, useState } from 'react';
import {
  Microphone, Stop, SpinnerGap, Sparkle, BookmarkSimple, Plus, X,
} from '@phosphor-icons/react';

/*
  SmartTextInput
  --------------
  Reusable enhanced input for the AI Chef modal's free-text fields.

  Props:
    value            (string)                        - controlled value
    onChange         (fn: newValue => void)          - controlled setter
    api              (axios instance)                - used for voice/AI parse
    field            ("avoid" | "instructions")       - drives labels + AI parse
    placeholder      (string)
    quickChips       ([{ label, hint }])              - static one-tap chips
    contextChips     ([{ label, hint, reason?, when? }]) - live contextual chips
    templates        ([{ label, text }])              - preset instruction templates
    dataTestid       (string)                         - root testid

  Behaviour:
    - Clicking a chip appends its `hint` to the current value (deduped, comma-sep).
    - Voice mic records → /api/voice-cmd/transcribe → appends transcript.
    - AI Parse ✨ button calls /api/meals/ai-chef/parse-input → replaces value
      with cleaned, expanded, comma-separated instructions.
    - Templates dropdown shows presets + any user-saved templates from
      localStorage under key `aichef_templates_<field>`.
*/

function chipsFromValue(v) {
  return (v || '')
    .split(/,|;/)
    .map(s => s.trim())
    .filter(Boolean);
}

function appendHint(existing, hint) {
  const cur = chipsFromValue(existing);
  const seen = new Set(cur.map(x => x.toLowerCase()));
  const clean = (hint || '').trim();
  if (!clean) return existing;
  if (seen.has(clean.toLowerCase())) return existing; // dedupe
  cur.push(clean);
  return cur.join(', ');
}

function removeChipAt(existing, index) {
  const cur = chipsFromValue(existing);
  cur.splice(index, 1);
  return cur.join(', ');
}

export default function SmartTextInput({
  value,
  onChange,
  api,
  field = 'instructions',
  placeholder = '',
  quickChips = [],
  contextChips = [],
  templates = [],
  dataTestid,
  compact = false,
}) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const wrapperRef = useRef(null);

  const storageKey = `aichef_templates_${field}`;

  // Load user templates
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      setSavedTemplates(raw ? JSON.parse(raw) : []);
    } catch (_) { setSavedTemplates([]); }
  }, [storageKey]);

  // Close template dropdown on outside click
  useEffect(() => {
    if (!tplOpen) return;
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setTplOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [tplOpen]);

  const currentChips = chipsFromValue(value);

  const logSignal = (type, ctx) => {
    // Fire-and-forget signal logging for the self-learning aggregator
    try {
      api.post('/meals/signals/log', { type, context: ctx || {} }).catch(() => { /* ignore */ });
    } catch (_) { /* ignore */ }
  };

  const addHint = (hint) => onChange(appendHint(value, hint));

  const removeChip = (idx) => onChange(removeChipAt(value, idx));

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunks.current = [];
      mediaRecorder.current.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      mediaRecorder.current.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        await handleTranscribe(blob);
      };
      mediaRecorder.current.start();
      setRecording(true);
    } catch (err) {
      console.error('Mic denied', err);
      alert('Microphone access denied. Please allow microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current?.state === 'recording') mediaRecorder.current.stop();
    setRecording(false);
  };

  const handleTranscribe = async (blob) => {
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'clip.webm');
      const res = await api.post('/voice-cmd/transcribe', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const txt = (res.data?.text || '').trim();
      if (txt) {
        // Append the transcript inline, then let user tap ✨ if they want it structured
        const merged = value ? `${value}, ${txt}` : txt;
        onChange(merged);
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Voice transcription failed.');
    }
    setProcessing(false);
  };

  const runAIParse = async () => {
    if (!value?.trim()) return;
    setAiBusy(true);
    try {
      const res = await api.post('/meals/ai-chef/parse-input', {
        field,
        text: value,
      });
      const cleaned = res.data?.cleaned;
      if (cleaned) onChange(cleaned);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'AI parse failed.');
    }
    setAiBusy(false);
  };

  const applyTemplate = (tpl) => {
    const tplText = typeof tpl === 'string' ? tpl : tpl?.text;
    const tplLabel = typeof tpl === 'string' ? '' : tpl?.label;
    if (!tplText) return;
    // Merge dedup instead of overwrite
    const cur = chipsFromValue(value);
    const additions = chipsFromValue(tplText);
    const seen = new Set(cur.map(x => x.toLowerCase()));
    additions.forEach(a => {
      if (!seen.has(a.toLowerCase())) { cur.push(a); seen.add(a.toLowerCase()); }
    });
    onChange(cur.join(', '));
    setTplOpen(false);
    if (tplLabel) logSignal('template_used', { label: tplLabel, field });
  };

  const saveCurrentAsTemplate = () => {
    const label = window.prompt('Template name?');
    if (!label || !value?.trim()) return;
    const next = [...savedTemplates, { label: label.trim(), text: value.trim() }];
    setSavedTemplates(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch (_) { /* ignore */ }
  };

  const deleteTemplate = (i) => {
    const next = savedTemplates.filter((_, idx) => idx !== i);
    setSavedTemplates(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch (_) { /* ignore */ }
  };

  // Auto-suggest chips: only show ones NOT already in current value
  const filterUnused = (list) => {
    const cur = new Set(currentChips.map(c => c.toLowerCase()));
    return (list || []).filter(c => {
      const hint = (c.hint || c.label || '').toLowerCase();
      return !cur.has(hint) && !cur.has((c.label || '').toLowerCase());
    });
  };

  const shownQuick = filterUnused(quickChips);
  const shownContext = filterUnused(contextChips);
  const combinedTemplates = [...templates, ...savedTemplates];

  return (
    <div ref={wrapperRef} data-testid={dataTestid} className="relative">
      {/* Text input + action buttons */}
      <div className="flex items-stretch gap-1.5">
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={dataTestid ? `${dataTestid}-input` : undefined}
          className={`flex-1 rounded-xl bg-white/5 border border-white/10 text-white outline-none focus:border-[#FF6B35]/50 ${compact ? 'px-3 py-2 text-[12px]' : 'px-4 py-3 text-sm'}`}
        />

        {/* Voice mic */}
        <button
          type="button"
          onClick={recording ? stopRecording : startRecording}
          disabled={processing}
          data-testid={dataTestid ? `${dataTestid}-mic` : undefined}
          title={recording ? 'Stop recording' : 'Dictate with voice'}
          className={`flex items-center justify-center rounded-xl border transition-all ${compact ? 'w-9' : 'w-11'} ${recording
            ? 'bg-red-500/20 border-red-500/50 text-red-300 animate-pulse'
            : processing
              ? 'bg-neutral-800 border-neutral-700 text-neutral-500'
              : 'bg-white/5 border-white/10 text-neutral-300 hover:text-white hover:border-white/30'}`}
        >
          {processing ? <SpinnerGap size={compact ? 14 : 16} className="animate-spin" />
            : recording ? <Stop size={compact ? 14 : 16} weight="fill" />
              : <Microphone size={compact ? 14 : 16} weight="fill" />}
        </button>

        {/* AI expand */}
        <button
          type="button"
          onClick={runAIParse}
          disabled={aiBusy || !value?.trim()}
          data-testid={dataTestid ? `${dataTestid}-ai-expand` : undefined}
          title="Clean up & expand my instructions with AI"
          className={`flex items-center justify-center rounded-xl border transition-all ${compact ? 'w-9' : 'w-11'} ${aiBusy
            ? 'bg-purple-500/20 border-purple-500/40 text-purple-300 animate-pulse'
            : 'bg-purple-500/10 border-purple-500/30 text-purple-200 hover:bg-purple-500/25 disabled:opacity-40'}`}
        >
          {aiBusy ? <SpinnerGap size={compact ? 14 : 16} className="animate-spin" /> : <Sparkle size={compact ? 14 : 16} weight="fill" />}
        </button>

        {/* Templates */}
        {(combinedTemplates.length > 0 || field === 'instructions') && (
          <button
            type="button"
            onClick={() => setTplOpen(o => !o)}
            data-testid={dataTestid ? `${dataTestid}-templates-btn` : undefined}
            title="Templates"
            className={`flex items-center justify-center rounded-xl border transition-all ${compact ? 'w-9' : 'w-11'} ${tplOpen
              ? 'bg-amber-500/20 border-amber-500/40 text-amber-200'
              : 'bg-white/5 border-white/10 text-neutral-300 hover:text-white hover:border-white/30'}`}
          >
            <BookmarkSimple size={compact ? 14 : 16} weight={tplOpen ? 'fill' : 'regular'} />
          </button>
        )}
      </div>

      {/* Templates dropdown */}
      {tplOpen && (
        <div className="absolute z-40 mt-1.5 right-0 w-72 max-h-72 overflow-auto rounded-xl border border-white/10 bg-neutral-900/95 backdrop-blur p-2 shadow-2xl"
          data-testid={dataTestid ? `${dataTestid}-templates-menu` : undefined}>
          <div className="flex items-center justify-between px-2 py-1">
            <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Templates</p>
            {value?.trim() && (
              <button onClick={saveCurrentAsTemplate}
                data-testid={dataTestid ? `${dataTestid}-save-template` : undefined}
                className="text-[10px] font-bold text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1">
                <Plus size={10} weight="bold" /> Save current
              </button>
            )}
          </div>
          {combinedTemplates.length === 0 && (
            <p className="px-2 py-2 text-[11px] text-neutral-500 italic">No templates yet. Type something, then tap &ldquo;Save current&rdquo;.</p>
          )}
          {combinedTemplates.map((t, i) => {
            const isUser = i >= templates.length;
            return (
              <div key={`${t.label}-${i}`} className="flex items-center gap-1 group">
                <button
                  onClick={() => applyTemplate(t)}
                  className="flex-1 text-left px-2 py-1.5 rounded-lg hover:bg-white/5 text-[12px] text-neutral-200"
                >
                  <div className="font-semibold">{t.label}</div>
                  <div className="text-[10px] text-neutral-500 line-clamp-1">{t.text}</div>
                </button>
                {isUser && (
                  <button onClick={() => deleteTemplate(i - templates.length)}
                    className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-red-300 p-1">
                    <X size={11} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Current chip pills (removable) */}
      {currentChips.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {currentChips.map((c, i) => (
            <span key={`${c}-${i}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-[#FF6B35]/15 border border-[#FF6B35]/30 text-[#FFB088] text-[10px] font-semibold">
              {c}
              <button onClick={() => removeChip(i)} className="hover:text-white p-0.5" title="Remove">
                <X size={9} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Quick-pick chips (static) */}
      {shownQuick.length > 0 && (
        <div className="mt-2">
          <p className="text-[9px] uppercase tracking-wider font-bold text-neutral-500 mb-1">Quick picks</p>
          <div className="flex flex-wrap gap-1">
            {shownQuick.map((c, i) => (
              <button
                key={`q-${i}`}
                type="button"
                onClick={() => { addHint(c.hint || c.label); logSignal('chip_used', { label: c.label, field }); }}
                data-testid={dataTestid ? `${dataTestid}-quick-${i}` : undefined}
                className="px-2 py-0.5 rounded-full text-[10px] font-semibold border border-white/10 bg-white/5 text-neutral-300 hover:text-white hover:border-white/25 hover:bg-white/10 transition-colors"
                title={c.hint}
              >
                + {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Contextual chips (dynamic) */}
      {shownContext.length > 0 && (
        <div className="mt-2">
          <p className="text-[9px] uppercase tracking-wider font-bold text-purple-300/80 mb-1">Suggested for you</p>
          <div className="flex flex-wrap gap-1">
            {shownContext.map((c, i) => (
              <button
                key={`c-${i}`}
                type="button"
                onClick={() => { addHint(c.hint || c.label); logSignal('ctx_chip_used', { label: c.label, field }); }}
                data-testid={dataTestid ? `${dataTestid}-ctx-${i}` : undefined}
                title={c.reason || c.hint || c.label}
                className="px-2 py-0.5 rounded-full text-[10px] font-semibold border border-purple-500/30 bg-purple-500/10 text-purple-200 hover:bg-purple-500/25 transition-colors inline-flex items-center gap-1"
              >
                + {c.label}
                {c.reason && <span className="text-[9px] text-purple-300/60">· {c.reason}</span>}
                {c.when && <span className="text-[9px] text-purple-300/60">· {c.when.slice(5)}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
